/**
 * The admin console's own client (D92).
 *
 * Deliberately not lib/api.ts: that one carries the business token and device
 * id and ends the business session on a 401. This one has its own token, its
 * own key, and its own way of signing out. The two never share a header.
 */
import type { AdminMe, AdminToken, AuditOut, BusinessOut, NewBusinessOut, OwnerReset, TotpStart } from "./types";

const BASE = "/v1/platform";
const TOKEN_KEY = "keystone.admin.token";

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "AdminApiError";
  }
}

/* ------------------------------------------------------------------ token */

export function readAdminToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeAdminToken(token: string | null) {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore — the sign-in then lasts until reload */
  }
}

type Listener = () => void;
const signedOutListeners = new Set<Listener>();

/** Called when the server says the admin token is no good. Returns an unsubscribe. */
export function onAdminSignedOut(listener: Listener): () => void {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

/* ---------------------------------------------------------------- request */

function messageFrom(status: number, body: unknown): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (status === 422) return "Check what you've entered and try again.";
  if (status === 429) return "Too many tries. Wait a few minutes.";
  return "Something went wrong. Try again.";
}

async function call<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean; signOutOn401?: boolean } = {},
): Promise<T> {
  const { method = "GET", body, auth = true, signOutOn401 = true } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = auth ? readAdminToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new AdminApiError(0, "Can't reach the server. Check your connection.");
  }

  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    /* no body, or not JSON */
  }

  if (!response.ok) {
    if (response.status === 401 && auth && signOutOn401) {
      writeAdminToken(null);
      signedOutListeners.forEach((listener) => listener());
    }
    throw new AdminApiError(response.status, messageFrom(response.status, data));
  }
  return data as T;
}

/* -------------------------------------------------------------------- api */

export const adminApi = {
  login: (body: { email: string; password: string; code?: string }) =>
    call<AdminToken>("/auth/login", { method: "POST", body, auth: false }),

  /**
   * A wrong current password is a 401 too, and must not end the sign-in. So
   * a 401 here is not taken as "token dead"; ask `me()` instead, which does.
   */
  changePassword: async (body: { current_password: string; new_password: string }) => {
    try {
      return await call<AdminToken>("/auth/password", { method: "POST", body, signOutOn401: false });
    } catch (error) {
      if (error instanceof AdminApiError && error.status === 401) await adminApi.me().catch(() => undefined);
      throw error;
    }
  },

  totpStart: () => call<TotpStart>("/auth/totp/start", { method: "POST" }),
  totpConfirm: (body: { code: string }) => call<AdminToken>("/auth/totp/confirm", { method: "POST", body }),
  me: () => call<AdminMe>("/auth/me"),

  businesses: () => call<BusinessOut[]>("/businesses"),
  addBusiness: (body: { name: string; owner_name: string; owner_email: string }) =>
    call<NewBusinessOut>("/businesses", { method: "POST", body }),
  patchBusiness: (id: string, body: { is_active?: boolean; note?: string | null }) =>
    call<BusinessOut>(`/businesses/${id}`, { method: "PATCH", body }),
  resetOwner: (id: string) => call<OwnerReset>(`/businesses/${id}/reset-owner`, { method: "POST" }),

  audit: (limit = 200) => call<AuditOut[]>(`/audit?limit=${limit}`),
};
